import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { PublicChoiceSpec } from "../protocol";

// A question answered with a name: a card name, a nonland card name or a creature type. The board
// asks it with a search over the release's names.json (mtg/cli/names.spell), so only a name on that
// list answers it.

export type NameQuestion = Extract<PublicChoiceSpec, { kind: "ChooseName" | "ChooseNonlandCardName" | "ChooseCreatureType" }>;

type Names = { cards: { name: string; land: boolean }[]; creature_types: string[] };

export function isNameQuestion(spec: PublicChoiceSpec | null): spec is NameQuestion {
  return spec?.kind === "ChooseName" || spec?.kind === "ChooseNonlandCardName" || spec?.kind === "ChooseCreatureType";
}

export function nameQuestionTitle(q: NameQuestion): string {
  if (q.values[0]) return q.values[0];
  return q.kind === "ChooseCreatureType" ? "Choose a creature type" : q.kind === "ChooseNonlandCardName" ? "Choose a nonland card name" : "Choose a card name";
}

export function nameQuestionNoun(q: NameQuestion): string {
  return q.kind === "ChooseCreatureType" ? "creature type" : "card name";
}

// Fetched once a page, the first time a question asks for a name.
export class NameLists {
  private names: Names | null = null;
  // One array a kind of question, so the picker sees the same options on every render.
  private readonly offered = new Map<NameQuestion["kind"], string[]>();
  private fetching = false;
  failed = false;

  constructor(private readonly onFetched: () => void) {}

  // Sorted; null until they're fetched, or if they couldn't be.
  offeredFor(q: NameQuestion): string[] | null {
    const names = this.names;
    if (!names) return null;
    const known = this.offered.get(q.kind);
    if (known) return known;
    const cards = q.kind === "ChooseNonlandCardName" ? names.cards.filter((c) => !c.land) : names.cards;
    const offered = q.kind === "ChooseCreatureType" ? [...names.creature_types].sort() : [...new Set(cards.map((c) => c.name))].sort();
    this.offered.set(q.kind, offered);
    return offered;
  }

  async fetch(): Promise<void> {
    if (this.names || this.fetching) return;
    this.fetching = true;
    this.failed = false;
    try {
      const r = await fetch("names.json");
      if (r.ok) this.names = (await r.json()) as Names;
      else this.failed = true;
    } catch {
      this.failed = true;
    }
    this.fetching = false;
    this.onFetched();
  }
}

// Names starting with the text first, then names holding it, each kept in the options' order.
function matching(options: string[], text: string): string[] {
  const q = text.trim().toLowerCase();
  if (!q) return options;
  const starting = options.filter((o) => o.toLowerCase().startsWith(q));
  const holding = options.filter((o) => !o.toLowerCase().startsWith(q) && o.toLowerCase().includes(q));
  return [...starting, ...holding];
}

// Typing searches the options and highlights the first match; ↑/↓ move the highlight, a click picks
// an option, and Enter or Submit confirms the highlighted one. `name-picked` says, as it changes, which option
// is highlighted (null before anything is typed), so the board's Confirm answers as Enter does;
// `name-confirmed` is Enter's answer. `unavailable`, when the options couldn't be fetched, lets any
// text through.
@customElement("name-picker")
export class NamePicker extends LitElement {
  // null while they're on their way.
  @property({ attribute: false }) options: string[] | null = null;
  @property({ type: Boolean }) unavailable = false;
  // What a name is here, as "card name".
  @property() noun = "name";

  @state() private text = "";
  @state() private active = 0;
  // Unset until the first render says what's picked, so a board never keeps the last question's.
  private lastPicked: string | null | undefined = undefined;

  static styles = css`
    :host { display: block; width: min(400px, 72vw); }
    .entry { display: flex; gap: 8px; align-items: center; }
    input {
      box-sizing: border-box; flex: 1; min-width: 0; font: inherit; font-size: 13px; padding: 8px 11px; border-radius: 3.5px;
      border: 1px solid #000; background: var(--input); color: #bbb;
    }
    input:focus { outline: none; border-color: var(--lavender); }
    button {
      flex: none; font: inherit; font-size: 14px; font-weight: 500; line-height: 1; padding: 8px 14px; border-radius: 5px; cursor: var(--cursor-pointer, pointer);
      color: var(--text-strong); background: var(--brand); border: 1px solid var(--brand-hi);
    }
    button:hover:not(:disabled) { background: var(--brand-hi); }
    button:disabled { opacity: 0.45; cursor: var(--cursor-default, not-allowed); }
    button:focus-visible { outline: 2px solid var(--lavender); outline-offset: 2px; }
    ul {
      list-style: none; margin: 8px 0 0; padding: 4px; max-height: 240px; overflow-y: auto;
      border: 1px solid var(--border); border-radius: 6px; background: var(--surface);
    }
    li { padding: 6px 9px; border-radius: 6px; font-size: 13px; color: var(--text); cursor: var(--cursor-pointer, pointer); }
    li:hover { background: var(--surface-3); }
    li[aria-selected="true"] { background: var(--lavender-soft); color: var(--lavender); }
    .note { font-size: 11.5px; color: var(--text-dim); margin-top: 8px; }
    .note.bad { color: var(--bad); }
  `;

  updated(changed: PropertyValues): void {
    if (changed.has("options") || changed.has("unavailable")) this.renderRoot.querySelector("input")?.focus();
    if (changed.has("active")) this.renderRoot.querySelector(`li[aria-selected="true"]`)?.scrollIntoView({ block: "nearest" });
    const now = this.picked;
    if (now !== this.lastPicked) {
      this.lastPicked = now;
      this.dispatchEvent(new CustomEvent<string | null>("name-picked", { detail: now, bubbles: true, composed: true }));
    }
  }

  // The highlighted match; while the options are unavailable, the text itself.
  get picked(): string | null {
    const text = this.text.trim();
    if (!text) return null;
    return this.unavailable ? text : this.shown[this.active] ?? null;
  }

  private get shown(): string[] {
    return this.options ? matching(this.options, this.text) : [];
  }

  private pick(option: string): void {
    this.text = option;
    this.active = 0;
  }

  private keyDown(e: KeyboardEvent): void {
    const shown = this.shown;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (shown.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      this.active = Math.max(0, Math.min(shown.length - 1, this.active + step));
    } else if (e.key === "Enter") {
      e.preventDefault();
      this.confirm();
    }
  }

  private confirm(): void {
    const chosen = this.picked;
    if (!chosen) return;
    this.pick(chosen);
    this.dispatchEvent(new CustomEvent<string>("name-confirmed", { detail: chosen, bubbles: true, composed: true }));
  }

  render() {
    const shown = this.shown;
    const listId = "name-options";
    const activeId = shown.length > 0 ? `name-option-${this.active}` : "";
    return html`
      <div class="entry">
        <input type="text" role="combobox" aria-label=${`Search for a ${this.noun}`} aria-autocomplete="list"
          aria-expanded=${shown.length > 0 ? "true" : "false"} aria-controls=${listId} aria-activedescendant=${activeId}
          placeholder=${this.options || this.unavailable ? `Search for a ${this.noun}` : "Loading names…"}
          ?disabled=${!this.options && !this.unavailable} .value=${this.text}
          @input=${(e: Event) => { this.text = (e.target as HTMLInputElement).value; this.active = 0; }}
          @keydown=${(e: KeyboardEvent) => this.keyDown(e)} />
        <button title="Choose the highlighted ${this.noun} (Enter)" ?disabled=${this.picked === null} @click=${() => this.confirm()}>Submit</button>
      </div>
      ${this.unavailable
        ? html`<div class="note bad">The names couldn't be loaded, so type the ${this.noun} exactly.</div>`
        : this.options && shown.length === 0
          ? html`<div class="note">No ${this.noun} matches “${this.text.trim()}”.</div>`
          : this.options
            ? html`<ul id=${listId} role="listbox" aria-label=${`${this.noun}s`}>
                ${shown.map((m, i) => html`<li id="name-option-${i}" role="option" aria-selected=${i === this.active ? "true" : "false"}
                    @mousedown=${(e: MouseEvent) => e.preventDefault()} @click=${() => this.pick(m)}>${m}</li>`)}
              </ul>`
            : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "name-picker": NamePicker;
  }
}
